import type { RegisterPlanState, Session } from "@/lib/api/attendance";

/** A plan as All sessions reads it: its state, or null for none; undefined while unread. */
type RegisterPlan = { state: RegisterPlanState } | null | undefined;

const PLAN_WORDS: Record<RegisterPlanState, string> = { draft: "Plan not published", changed: "Changed, not published", published: "Published" };

/** A stored course-local date is a date, not an instant in the browser's zone. */
export function registerDay(date: string, currentYear: number): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
    ...(Number(date.slice(0, 4)) !== currentYear ? { year: "numeric" } : {}),
  }).format(new Date(`${date}T12:00:00Z`));
}

export function sessionRegisterStates(session: Session, plan: RegisterPlan, teaching: boolean, currentYear: number): string[] {
  const states: string[] = [];
  const change = session.latest_change;
  if (session.status === "held") states.push("Held");
  else if (session.status === "cancelled") states.push(change?.kind === "cancel" && change.note ? `Cancelled · ${change.note}` : "Cancelled");
  else if (session.clock_phase === "status_pending") states.push("Ended, not held");
  if (session.status !== "cancelled" && change?.kind === "move") {
    states.push([change.from_date ? `Moved from ${registerDay(change.from_date, currentYear)}` : "Moved", change.note].filter(Boolean).join(" · "));
  } else if (session.status !== "cancelled" && change?.kind === "reinstate") {
    states.push(["Reinstated", change.note].filter(Boolean).join(" · "));
  }
  if (teaching) {
    if (session.roll === "not_recorded") states.push("Roll not recorded");
  }
  if (teaching && session.status !== "cancelled") {
    if (plan === undefined) states.push("Plan not read");
    else if (!plan) states.push("No plan yet");
    else states.push(PLAN_WORDS[plan.state]);
  }
  return states;
}

export interface SessionRegisterRow { session: Session; plan: RegisterPlan }

/** A single unsettled row separates runs. The caller unfolds runs during Find. */
export function settledSessionRuns<T extends SessionRegisterRow>(rows: T[], teaching: boolean, openSessionId: string | null): T[][] {
  const result: T[][] = [];
  let run: T[] = [];
  const flush = () => { if (run.length) result.push(run); run = []; };
  for (const row of rows) {
    const settled = row.session.id !== openSessionId && row.session.status === "held"
      && row.session.clock_phase === "status_pending"
      && (!teaching || row.session.roll === "recorded" && row.plan?.state === "published");
    if (settled) run.push(row);
    else { flush(); result.push([row]); }
  }
  flush();
  return result;
}
